import type { ChatUi } from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../shared/ui/action.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { GenUiEntry } from '../../shared/ui/registry.js';
import { MobilityPlannerCard } from './ui/mobility_planner.js';

type MobilityPlannerUi = Extract<ChatUi, { kind: 'mobility_planner' }>;

/**
 * A busca volta por `interaction` (o challenge do card + a
 * `MobilityPlannerSearch`): o agente agenda com os `ref`s escolhidos, sem o LLM
 * reinterpretar o texto. O eco é a fala que a pessoa aparenta ter dito — por
 * isso é traduzido aqui, num componente (o `render` do catálogo não roda hook).
 */
function MobilityPlannerEntry({
  ui,
  note,
  readOnly,
  superseded,
  replay,
  dispatch,
}: Readonly<{
  ui: MobilityPlannerUi;
  note?: string;
  readOnly?: boolean;
  superseded?: boolean;
  replay?: boolean;
  dispatch: GenUiDispatch;
}>) {
  const { t } = useGenUiI18n();
  return (
    <MobilityPlannerCard
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      {...(superseded ? { superseded } : {})}
      {...(replay ? { replay } : {})}
      onSearch={(search) => {
        dispatch({
          type: 'interaction',
          interactionId: ui.challengeId,
          text: search.destination
            ? t(
                search.tripType === 'round_trip'
                  ? 'mobilityPlanner.searchTextRoundTrip'
                  : 'mobilityPlanner.searchText',
                { from: search.origin.title, to: search.destination.title },
              )
            : t('mobilityPlanner.searchTextNoDestination', { from: search.origin.title }),
          data: search,
        });
      }}
    />
  );
}

/** Entrada de catálogo do `mobility_planner` (slot chat). */
export const mobilityPlannerEntry: GenUiEntry<MobilityPlannerUi> = {
  kind: 'mobility_planner',
  slot: 'chat',
  policy: { productOf: () => 'mobility' },
  render: ({ ui, note, readOnly, superseded, replay, dispatch }) => (
    <MobilityPlannerEntry
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      {...(superseded ? { superseded } : {})}
      {...(replay ? { replay } : {})}
      dispatch={dispatch}
    />
  ),
};
